import { Container, T_ResourceCache } from '@cgs/wge';
import { CustomGamesResourceCache } from '@cgs/common';
import { htmlTemplateSystem } from '../html-template-system/html-template-system';
import { T_Translations, Translations } from '../translations';
import { IAlertManager } from './i_alert_manager';

export class AlertManager implements IAlertManager {
  private resourcePath: string = 'alert';
  private template: string | null = null;
  private unsubscribe: (() => void)[] = [];
  private readonly resourceUrl: string;

  private readonly translations: Translations;

  constructor(container: Container, jsonUrl: string) {
    const gameResourceCache = container.resolve<CustomGamesResourceCache>(T_ResourceCache);

    this.resourceUrl = gameResourceCache.getUrl(jsonUrl);
    this.translations = container.resolve<Translations>(T_Translations);
    this.preloadData();
  }

  public show(params: {
    title?: string;
    text: string;
    closeButton?: string;
    reloadApp?: boolean;
  }): Promise<void> {
    if (!this.template || !this.translations) {
      return Promise.resolve();
    }

    if (this.isAlertOpened) {
      this.closeAlert(false);
    }

    const dialogContainer = this.dialogContainer;

    if (dialogContainer) {
      dialogContainer.insertAdjacentHTML(
        'beforeend',
        htmlTemplateSystem.build(this.template, {
          title: params.title || '',
          text: params.text || '',
          closeButton: params.closeButton || '',
          close: this.translations.get('close'),
        })
      );

      return this.handleAlertClose(params.reloadApp);
    }

    return Promise.resolve();
  }

  public showConnectionError(): Promise<void> {
    return this.show({
      title: this.getTranslation('error'),
      text: this.getTranslation('connection-error'),
    });
  }

  public showUnhandled(): Promise<void> {
    return this.show({
      title: this.getTranslation('error'),
      text: this.getTranslation('something-went-wrong-long'),
    });
  }

  public showSessionExpired(): Promise<void> {
    return this.show({
      text: this.getTranslation('error'),
      title: this.getTranslation('session-expired'),
    });
  }

  public getTranslation(key: string, rollback: string = ''): string {
    return this.translations.get(key) || rollback;
  }

  private get dialogContainer(): Element | null {
    return document.getElementById('dialogs');
  }

  private get alertElement(): Element | null {
    return document.getElementById('alert');
  }

  private get isAlertOpened(): boolean {
    return !!this.alertElement;
  }

  private handleAlertClose(reloadApp: boolean = true): Promise<void> {
    return new Promise((resolve) => {
      const closeElements = document.querySelectorAll("[data-name='close-alert']");
      const onClose = () => {
        this.closeAlert(reloadApp);
        resolve();
      };

      closeElements.forEach((element) => {
        element.addEventListener('click', onClose);
        this.unsubscribe.push(() => element.removeEventListener('click', onClose));
      });
    });
  }

  private closeAlert(reloadApp: boolean = false): void {
    const alertElement = this.alertElement;
    alertElement && this.dialogContainer?.removeChild(this.alertElement);
    this.unsubscribe.forEach((callback) => callback());
    this.unsubscribe = [];

    if (reloadApp) {
      window.location.reload();
    }
  }

  private async preloadData(): Promise<void> {
    try {
      this.template = await fetch(this.getFilePath('template.hbs')).then((response) =>
        response.ok ? response.text() : null
      );
    } catch {
      //
    }
  }

  private getFilePath(path: string): string {
    return `${this.resourceUrl}/${this.resourcePath}/${path}`;
  }
}
